import React, { useMemo } from "react";
import { useMercuryContext } from "../../../context";
import { handleApiError } from "common/utils/handleApiError";
import { checkPermissions } from "common/utils/checkPermissions";
import { RulesManager } from "stories/rules";
import { invoiceTypesApi, InvoiceTypeRule, INVOICE_TYPES_PERMISSION, TARGET_ENTITY_HELP, useInvoiceTypeVocabulary } from "../invoiceTypes.api";

interface ManageRulesTabProps {
  typeId: string;
  typeActive?: boolean;
}

/** The rules of one invoice type: the shared manager over the invoicing API. */
const ManageRulesTab: React.FC<ManageRulesTabProps> = ({ typeId, typeActive = true }) => {
  const { invoicingClient, userDetails } = useMercuryContext();
  const api = useMemo(() => invoiceTypesApi(invoicingClient).rules(typeId), [invoicingClient, typeId]);
  const vocabulary = useInvoiceTypeVocabulary(invoicingClient, (error) => handleApiError(error, "Failed to load rule vocabulary"));

  const userPermissions = userDetails?.permissions || [];
  const permission = (action: "create" | "edit" | "delete") =>
    checkPermissions(userPermissions, [{ ...INVOICE_TYPES_PERMISSION, action }]);

  return (
    <RulesManager<InvoiceTypeRule>
      api={api}
      tableId="invoice-type-rules"
      vocabulary={vocabulary}
      permissions={{ canCreate: permission("create"), canEdit: permission("edit"), canDelete: permission("delete") }}
      definitionActive={typeActive}
      targetHelp={TARGET_ENTITY_HELP}
      fieldPathHint={() => ({ extra: "Dot notation on the target, e.g. customVendorPLPMetadata.catToolProjectType" })}
      introDescription="Invoice types are evaluated lowest priority first; the first whose active rules all match decides the line items. The fallback type applies when none does."
    />
  );
};

export default ManageRulesTab;
